/**
 * What every field of a template guide IS, per page type, so the editor can give it the right
 * control.
 *
 * Mirrors the site's `src/types/resources.ts` (SkillsArticle, HiringGuide, CareerArticle and the
 * GuideEnvelopeFields they share) plus the fields the structured generator adds: `image` on steps
 * and sections, `figures`, `charts`, `additionalSections`, and `afterSection` on every table. The
 * backend's GuideTemplateSchemaParityTest holds the generator to the site's types; this map is the
 * third copy and has no test against the site, so a field the site gains appears here as an unknown
 * key and falls back to the JSON editor rather than disappearing. That fallback is the point:
 * nothing the map does not know about is ever hidden or dropped.
 *
 * The key order of each map is the order the editor shows the sections in, which follows the
 * order the template draws them.
 */

export interface Option {
  value: string;
  label: string;
}

interface Base {
  label: string;
  help?: string;
}

export type FieldSpec =
  | (Base & {
      kind: 'text';
      multiline?: boolean;
      /** A prose field: may carry <a href>, <strong> and <em>, paragraphs split by a blank line. */
      rich?: boolean;
      required?: boolean;
      /** Native input type, for the one date field. */
      inputType?: 'date';
    })
  | (Base & { kind: 'number'; integer?: boolean })
  | (Base & { kind: 'select'; options: Option[] })
  | (Base & { kind: 'multiSelect'; options: Option[]; min?: number; max?: number })
  | (Base & { kind: 'boolean' })
  | (Base & { kind: 'stringList'; itemLabel: string; min?: number; max?: number })
  | (Base & {
      kind: 'object';
      fields: Record<string, FieldSpec>;
      /** The object itself carries `src` and `alt` (a figure), edited with the image control. */
      inlineImage?: boolean;
      /** Keys owned by another control (the banner), kept as they are and not shown. */
      hidden?: string[];
    })
  | (Base & {
      kind: 'list';
      itemLabel: string;
      item: FieldSpec;
      /** Which field of an object item names it in its header. */
      titleKey?: string;
      min?: number;
      max?: number;
    })
  | (Base & { kind: 'table' })
  | (Base & { kind: 'image' })
  | (Base & { kind: 'headingRef'; emptyLabel: string });

/* ── Builders ─────────────────────────────────────────────────────────────── */

const text = (label: string, extra: Partial<Extract<FieldSpec, { kind: 'text' }>> = {}): FieldSpec => ({
  kind: 'text',
  label,
  ...extra,
});
const required = (label: string): FieldSpec => text(label, { required: true });
const long = (label: string): FieldSpec => text(label, { multiline: true });
const prose = (label: string, extra: Partial<Extract<FieldSpec, { kind: 'text' }>> = {}): FieldSpec =>
  text(label, { multiline: true, rich: true, ...extra });
const strings = (label: string, itemLabel = 'Item', extra: { min?: number; max?: number; help?: string } = {}): FieldSpec => ({
  kind: 'stringList',
  label,
  itemLabel,
  ...extra,
});
const obj = (label: string, fields: Record<string, FieldSpec>, extra: Partial<Extract<FieldSpec, { kind: 'object' }>> = {}): FieldSpec => ({
  kind: 'object',
  label,
  fields,
  ...extra,
});
const list = (
  label: string,
  itemLabel: string,
  item: FieldSpec,
  titleKey?: string,
  extra: { min?: number; max?: number; help?: string } = {},
): FieldSpec => ({ kind: 'list', label, itemLabel, item, titleKey, ...extra });
const placeAfter = (label = 'Place after section'): FieldSpec => ({
  kind: 'headingRef',
  label,
  emptyLabel: 'Default position',
});

const IMAGE: FieldSpec = { kind: 'image', label: 'Image' };
const TABLE = (label: string): FieldSpec => ({ kind: 'table', label });

const headingContent = (label: string): FieldSpec =>
  obj(label, { heading: required('Heading'), content: prose('Content') });

const STEP = obj('Step', {
  heading: required('Heading'),
  description: prose('Description'),
  image: IMAGE,
});
const steps = (label: string): FieldSpec =>
  obj(label, { heading: text('Heading'), steps: list('Steps', 'Step', STEP, 'heading') });

/** A career section, and the same shape as `additionalSections` on the other two types. */
const SECTION = obj('Section', {
  heading: required('Heading'),
  content: prose('Content'),
  bullets: strings('Bullets', 'Bullet'),
  image: IMAGE,
});

const records = (label: string, itemLabel: string, fields: Record<string, FieldSpec>, titleKey?: string) =>
  list(label, itemLabel, obj(itemLabel, fields), titleKey);

const QUICK_SUMMARY_ROWS = records('Quick summary', 'Row', { label: required('Label'), value: required('Value') }, 'label');

const CHIP_GROUPS = records(
  'Skill groups',
  'Group',
  {
    label: required('Label'),
    variant: {
      kind: 'select',
      label: 'Variant',
      options: [
        { value: 'core', label: 'Core (must-have)' },
        { value: 'default', label: 'Default' },
        { value: 'muted', label: 'Muted' },
      ],
    },
    items: strings('Chips', 'Chip'),
  },
  'label',
);

const RESOURCE_TABLES = list('Tables', 'Table', TABLE('Table'), 'heading');

const CHARTS = list(
  'Charts',
  'Chart',
  obj('Chart', {
    title: required('Title'),
    after: placeAfter(),
    unit: text('Unit', { help: 'Printed after each value, e.g. LPA or %. Leave blank for none.' }),
    rows: list(
      'Bars',
      'Bar',
      obj('Bar', {
        label: required('Label'),
        value: { kind: 'number', label: 'Value' },
        display: text('Display text', { help: 'Shown instead of the value, e.g. "4 to 6 LPA".' }),
      }),
      'label',
      { min: 2, max: 6 },
    ),
  }),
  'title',
);

const FIGURES = list(
  'Figures',
  'Figure',
  obj('Figure', { afterHeading: placeAfter() }, { inlineImage: true }),
  'alt',
);

const EXPERT_QUOTES = records(
  'Expert quotes',
  'Quote',
  { quote: long('Quote'), author: required('Author'), role: text('Role') },
  'author',
);

const ADDITIONAL_SECTIONS = list('Additional sections', 'Section', SECTION, 'heading');

const SEO = obj(
  'SEO',
  {
    title: required('Search title'),
    description: long('Meta description'),
    keywords: text('Keywords'),
    datePublished: text('Date published', { inputType: 'date' }),
    ogImageAlt: text('Banner alt text', { help: 'What the banner image shows. Set the banner itself under Hero image.' }),
    faqs: records('FAQs', 'FAQ', { question: required('Question'), answer: long('Answer') }, 'question'),
  },
  { hidden: ['ogImage', 'dateModified'] },
);

const HOOK = obj('Hook', { headline: required('Headline'), subtext: long('Subtext') });

/** The envelope every template reads (Guides brief §2.1). */
const ENVELOPE: Record<string, FieldSpec> = {
  audience: {
    kind: 'select',
    label: 'Audience',
    options: [
      { value: 'jobseeker', label: 'Job seekers' },
      { value: 'employer', label: 'Employers' },
      { value: 'both', label: 'Both' },
    ],
  },
  topics: {
    kind: 'multiSelect',
    label: 'Topics',
    min: 1,
    max: 3,
    // The site's guideTaxonomy slugs; the backend's schema enum is the same twelve.
    options: [
      { value: 'showroom-sales', label: 'Showroom sales' },
      { value: 'cad-design', label: 'CAD design' },
      { value: 'gemology', label: 'Gemology' },
      { value: 'metals-hallmarking', label: 'Metals and hallmarking' },
      { value: 'merchandising', label: 'Merchandising' },
      { value: 'store-operations', label: 'Store operations' },
      { value: 'onboarding', label: 'Onboarding' },
      { value: 'interviews', label: 'Interviews' },
      { value: 'salary-benchmarks', label: 'Salary benchmarks' },
      { value: 'career-growth', label: 'Career growth' },
      { value: 'd2c-ecommerce', label: 'D2C and e-commerce' },
      { value: 'luxury-brands', label: 'Luxury brands' },
    ],
  },
  level: {
    kind: 'select',
    label: 'Level',
    options: [
      { value: 'beginner', label: 'Beginner' },
      { value: 'intermediate', label: 'Intermediate' },
      { value: 'advanced', label: 'Advanced' },
    ],
  },
  keyTakeaways: strings('Key takeaways', 'Takeaway', { min: 3, max: 5 }),
};

const RELATED: Record<string, FieldSpec> = {
  relatedResourceSlugs: strings('Related guides', 'Guide slug'),
  relatedJobSlugs: strings('Related job links', 'Job link'),
};

const SKILL: Record<string, FieldSpec> = {
  hook: HOOK,
  introduction: prose('Introduction'),
  quickSummaryRows: QUICK_SUMMARY_ROWS,
  whatIsIt: headingContent('What is it'),
  whyItMatters: obj('Why it matters', {
    heading: required('Heading'),
    content: prose('Content'),
    bullets: strings('Bullets', 'Bullet'),
  }),
  salaryTable: TABLE('Salary table'),
  skillGroupsHeading: text('Skill groups heading'),
  skillGroups: CHIP_GROUPS,
  certificationTable: TABLE('Certification table'),
  keyDataPoints: obj('Key data points', { heading: text('Heading'), points: strings('Points', 'Point') }),
  stepByStep: steps('Step by step'),
  realWorldExample: headingContent('Real-world example'),
  commonMistakes: strings('Common mistakes', 'Mistake'),
  proTips: strings('Pro tips', 'Tip'),
  careerImpact: headingContent('Career impact'),
  additionalSections: ADDITIONAL_SECTIONS,
  resourceTables: RESOURCE_TABLES,
  charts: CHARTS,
  figures: FIGURES,
  faqTitle: text('FAQ heading'),
  ...ENVELOPE,
  seo: SEO,
  ...RELATED,
};

const HIRING: Record<string, FieldSpec> = {
  hook: HOOK,
  introduction: prose('Introduction'),
  quickSummary: obj('Quick summary', {
    roleType: text('Role type'),
    experience: text('Experience'),
    avgHiringTime: text('Average hiring time'),
    keySkills: text('Key skills'),
    hiringChannels: text('Hiring channels'),
    interviewRounds: text('Interview rounds'),
  }),
  quickSummaryRows: QUICK_SUMMARY_ROWS,
  hideRoleOverview: { kind: 'boolean', label: 'Hide the role overview' },
  roleOverview: headingContent('Role overview'),
  responsibilityGroups: records('Responsibilities', 'Group', { heading: required('Heading'), items: strings('Items', 'Responsibility') }, 'heading'),
  hideSkillsSection: { kind: 'boolean', label: 'Hide the skills section' },
  skillsRequired: strings('Skills required', 'Skill'),
  categorizedSkills: obj('Hard and soft skills', { hard: strings('Hard skills', 'Skill'), soft: strings('Soft skills', 'Skill') }),
  skillGroups: CHIP_GROUPS,
  qualifications: records('Qualifications', 'Segment', { segment: required('Segment'), points: strings('Points', 'Point') }, 'segment'),
  hideHiringProcess: { kind: 'boolean', label: 'Hide the hiring process' },
  hiringProcess: steps('Hiring process'),
  sourcingChannels: strings('Sourcing channels', 'Channel'),
  interviewQuestions: strings('Interview questions', 'Question'),
  categorizedInterviewQuestions: records(
    'Interview questions by category',
    'Category',
    { category: required('Category'), questions: strings('Questions', 'Question') },
    'category',
  ),
  redFlags: strings('Red flags', 'Red flag'),
  commonMistakesDetailed: obj('Common mistakes', {
    heading: text('Heading'),
    items: records('Mistakes', 'Mistake', { title: required('Title'), description: prose('Description'), subPoints: strings('Sub-points', 'Point') }, 'title'),
  }),
  salaryInsights: headingContent('Salary insights'),
  salaryTable: records('Salary table', 'Row', { level: required('Level'), range: required('Range') }, 'level'),
  salaryTierCards: records(
    'Salary tiers',
    'Tier',
    {
      level: required('Level'),
      yearsExp: text('Years of experience'),
      range: text('Range'),
      roles: strings('Roles', 'Role'),
      perks: strings('Perks', 'Perk'),
    },
    'level',
  ),
  comparisonTable: obj('Comparison table', {
    heading: text('Heading'),
    col1Header: text('First column header'),
    col2Header: text('Second column header'),
    rows: records('Rows', 'Row', { label: required('Label'), col1: text('First column'), col2: text('Second column') }, 'label'),
    conclusion: prose('Conclusion'),
  }),
  hiringChecklist: records('Hiring checklist', 'Phase', { phase: required('Phase'), items: strings('Items', 'Item') }, 'phase'),
  benefitsHeading: text('Benefits heading'),
  benefits: records('Benefits', 'Benefit', { title: required('Title'), description: prose('Description'), subPoints: strings('Sub-points', 'Point') }, 'title'),
  trainingModules: obj('Training modules', {
    heading: text('Heading'),
    modules: records(
      'Modules',
      'Module',
      {
        number: { kind: 'number', label: 'Number', integer: true },
        title: required('Title'),
        description: prose('Description'),
        subModules: records('Sub-modules', 'Sub-module', { title: required('Title'), points: strings('Points', 'Point') }, 'title'),
      },
      'title',
    ),
  }),
  timelinePlan: obj('Timeline plan', {
    heading: text('Heading'),
    rows: records('Rows', 'Row', { timeline: required('Timeline'), focusArea: text('Focus area'), outcome: text('Outcome'), assessment: text('Assessment') }, 'timeline'),
  }),
  kpiTable: obj('KPI table', {
    heading: text('Heading'),
    rows: records('Rows', 'KPI', { kpi: required('KPI'), whyItMatters: text('Why it matters'), howToMeasure: text('How to measure') }, 'kpi'),
  }),
  bestPractices: obj('Best practices', {
    heading: text('Heading'),
    items: records('Practices', 'Practice', { title: required('Title'), description: prose('Description'), points: strings('Points', 'Point') }, 'title'),
  }),
  techTools: obj('Technology tools', {
    heading: text('Heading'),
    items: records(
      'Tool categories',
      'Category',
      {
        category: required('Category'),
        description: prose('Description'),
        points: strings('Points', 'Point'),
        buyingCriteria: strings('Buying criteria', 'Criterion'),
        examples: strings('Examples', 'Example'),
      },
      'category',
    ),
  }),
  guidanceSection: obj('Guidance', { heading: text('Heading'), items: strings('Items', 'Tip') }),
  keyDataPoints: obj('Key data points', { heading: text('Heading'), points: strings('Points', 'Point') }),
  luxuryBrandsSection: obj('Luxury brands', { heading: text('Heading'), content: prose('Content'), points: strings('Points', 'Point') }),
  expertQuotes: EXPERT_QUOTES,
  additionalSections: ADDITIONAL_SECTIONS,
  resourceTables: RESOURCE_TABLES,
  charts: CHARTS,
  figures: FIGURES,
  ...ENVELOPE,
  seo: SEO,
  ...RELATED,
};

const CAREER: Record<string, FieldSpec> = {
  hook: HOOK,
  introduction: prose('Introduction'),
  quickSummaryRows: QUICK_SUMMARY_ROWS,
  sections: list('Sections', 'Section', SECTION, 'heading'),
  commonMistakes: strings('Common mistakes', 'Mistake'),
  proTips: strings('Pro tips', 'Tip'),
  expertQuotes: EXPERT_QUOTES,
  resourceTables: RESOURCE_TABLES,
  charts: CHARTS,
  figures: FIGURES,
  ...ENVELOPE,
  seo: SEO,
  ...RELATED,
};

export const FIELD_MAP: Record<string, Record<string, FieldSpec>> = {
  'skill-guide': SKILL,
  'hiring-guide': HIRING,
  'career-guide': CAREER,
};

/** The spec for one top-level key, or undefined when the map does not know it. */
export function specFor(pageType: string, key: string): FieldSpec | undefined {
  return FIELD_MAP[pageType]?.[key];
}

/* ── Shape ────────────────────────────────────────────────────────────────── */

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Whether a value has the shape its spec edits.
 *
 * Shallow on purpose: an object or list is checked for being an object or list, and each child is
 * checked again when it renders. So a guide with one malformed step gets the JSON editor for that
 * step alone, not for the whole hiring process.
 */
export function conforms(spec: FieldSpec, value: unknown): boolean {
  if (value === undefined || value === null) return true;
  switch (spec.kind) {
    case 'text':
    case 'select':
    case 'headingRef':
      return typeof value === 'string';
    case 'number':
      // An empty string is what a cleared number field holds mid-edit; it is flagged, not refused.
      return typeof value === 'number' || value === '';
    case 'boolean':
      return typeof value === 'boolean';
    case 'multiSelect':
    case 'stringList':
      return Array.isArray(value) && value.every((v) => typeof v === 'string');
    case 'object':
    case 'image':
      return isPlainObject(value);
    case 'list':
      return Array.isArray(value);
    case 'table':
      return (
        isPlainObject(value) &&
        (value.columns === undefined || Array.isArray(value.columns)) &&
        (value.rows === undefined ||
          (Array.isArray(value.rows) && value.rows.every((r) => Array.isArray(r))))
      );
    default:
      return false;
  }
}

/** A starting value for a new field or list item: the shape, empty. */
export function emptyValue(spec: FieldSpec): unknown {
  switch (spec.kind) {
    case 'text':
      return '';
    case 'number':
      return 0;
    case 'select':
      return spec.options[0]?.value ?? '';
    case 'headingRef':
      return '';
    case 'boolean':
      return false;
    case 'multiSelect':
    case 'stringList':
      return [];
    case 'image':
      return null;
    case 'table':
      return { heading: '', columns: ['Column 1', 'Column 2'], rows: [['', '']] };
    case 'list':
      return Array.from({ length: spec.min ?? 0 }, () => emptyValue(spec.item));
    case 'object': {
      const out: Record<string, unknown> = spec.inlineImage ? { src: '', alt: '' } : {};
      for (const [key, child] of Object.entries(spec.fields)) {
        // Optional slots start absent rather than as an empty placeholder the site would draw.
        if (child.kind === 'image' || child.kind === 'headingRef' || child.kind === 'boolean') continue;
        if (child.kind === 'select') continue;
        out[key] = emptyValue(child);
      }
      return out;
    }
    default:
      return '';
  }
}

/** The seed for a top-level section added from the "not in this guide yet" row. */
export function seedFor(pageType: string, key: string): unknown {
  const spec = specFor(pageType, key);
  if (spec) return emptyValue(spec);
  // The old inference: the two list-shaped required keys, prose for the rest.
  return key === 'relatedResourceSlugs' || key === 'sections' ? [] : '';
}

/* ── Headings ─────────────────────────────────────────────────────────────── */

/** Tables carry a heading too, but a table is placed after a section, never the other way round. */
const NOT_SECTIONS = new Set(['seo', 'hook', 'resourceTables', 'salaryTable', 'certificationTable']);

/**
 * Every section heading in the guide, in order: what `afterSection`, a chart's `after` and a
 * figure's `afterHeading` can point at.
 */
export function collectHeadings(obj: Record<string, unknown>): string[] {
  const out: string[] = [];
  const take = (v: unknown) => {
    if (isPlainObject(v) && typeof v.heading === 'string' && v.heading.trim()) out.push(v.heading.trim());
  };
  for (const [key, value] of Object.entries(obj)) {
    if (NOT_SECTIONS.has(key)) continue;
    if (Array.isArray(value)) value.forEach(take);
    else take(value);
  }
  return [...new Set(out)];
}

/** How the site matches a placement against a heading: case, spacing and trailing punctuation. */
export function normaliseHeading(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim().replace(/[.:;!?]+$/, '');
}

/* ── Validation ───────────────────────────────────────────────────────────── */

export interface FieldIssue {
  /** Dot path from the guide root, e.g. `hiringProcess.steps.1.heading`. */
  path: string;
  /** Human trail for the validation bar, e.g. "Hiring process, Step 2, Heading". */
  where: string;
  /** `error` blocks publishing; `warning` is worth fixing. */
  level: 'error' | 'warning';
  message: string;
}

interface Walk {
  headings: Set<string>;
  issues: FieldIssue[];
}

function push(w: Walk, path: string, where: string[], level: FieldIssue['level'], message: string) {
  w.issues.push({ path, where: where.filter(Boolean).join(', '), level, message });
}

function checkPlacement(w: Walk, value: unknown, path: string, where: string[]) {
  if (typeof value === 'string' && value.trim() && !w.headings.has(normaliseHeading(value))) {
    push(w, path, where, 'warning', 'No section has this heading, so the site places it in its default position.');
  }
}

function checkImage(w: Walk, value: unknown, path: string, where: string[]) {
  if (!isPlainObject(value)) return;
  const src = typeof value.src === 'string' ? value.src.trim() : '';
  const alt = typeof value.alt === 'string' ? value.alt.trim() : '';
  if (src && !alt) {
    push(w, `${path}.alt`, where, 'error', 'Alt text is required: describe what the image shows.');
  }
}

function walk(spec: FieldSpec, value: unknown, path: string, where: string[], w: Walk) {
  if (!conforms(spec, value)) return; // the JSON fallback reports its own parse errors
  switch (spec.kind) {
    case 'text':
      if (spec.required && !(typeof value === 'string' && value.trim())) {
        push(w, path, where, 'warning', `${spec.label} is empty.`);
      }
      return;
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        push(w, path, where, 'error', 'Enter a number.');
      }
      return;
    case 'headingRef':
      checkPlacement(w, value, path, where);
      return;
    case 'image':
      checkImage(w, value, path, where);
      return;
    case 'multiSelect':
    case 'stringList': {
      if (!Array.isArray(value)) return;
      const n = value.length;
      if (spec.min !== undefined && spec.max !== undefined && n > 0 && (n < spec.min || n > spec.max)) {
        push(w, path, where, 'warning', `Has ${n}; aim for ${spec.min} to ${spec.max}.`);
      }
      return;
    }
    case 'object': {
      if (!isPlainObject(value)) return;
      if (spec.inlineImage) {
        if (!(typeof value.src === 'string' && value.src.trim())) {
          push(w, `${path}.src`, where, 'error', 'Choose an image, or remove this figure.');
        }
        checkImage(w, value, path, where);
      }
      for (const [key, child] of Object.entries(spec.fields)) {
        walk(child, value[key], `${path}.${key}`, [...where, child.label], w);
      }
      return;
    }
    case 'list': {
      if (!Array.isArray(value)) return;
      if (spec.min !== undefined && value.length < spec.min) {
        push(w, path, where, 'warning', `Needs at least ${spec.min}.`);
      }
      if (spec.max !== undefined && value.length > spec.max) {
        push(w, path, where, 'warning', `Has ${value.length}; the most it can show is ${spec.max}.`);
      }
      value.forEach((item, i) =>
        walk(spec.item, item, `${path}.${i}`, [...where, `${spec.itemLabel} ${i + 1}`], w),
      );
      return;
    }
    case 'table': {
      if (!isPlainObject(value)) return;
      const columns = Array.isArray(value.columns) ? value.columns.length : 0;
      const rows = Array.isArray(value.rows) ? (value.rows as unknown[][]) : [];
      rows.forEach((row, i) => {
        if (row.length !== columns) {
          push(w, `${path}.rows.${i}`, [...where, `Row ${i + 1}`], 'warning', `Row ${i + 1} has ${row.length} cell(s) for ${columns} column(s).`);
        }
      });
      checkPlacement(w, value.afterSection, `${path}.afterSection`, [...where, 'Placement']);
      return;
    }
    default:
  }
}

/** Every inline problem in a structured guide, keyed by path for the field that shows it. */
export function validateStructured(obj: Record<string, unknown>, pageType: string): FieldIssue[] {
  const map = FIELD_MAP[pageType];
  if (!map) return [];
  const w: Walk = { headings: new Set(collectHeadings(obj).map(normaliseHeading)), issues: [] };
  for (const [key, value] of Object.entries(obj)) {
    const spec = map[key];
    if (spec) walk(spec, value, key, [spec.label], w);
  }
  return w.issues;
}

/** Top-level keys in display order: the map's order for known keys, then unknown ones. */
export function structuredOrder(obj: Record<string, unknown>, pageType: string, skip: Set<string>): string[] {
  const map = FIELD_MAP[pageType] ?? {};
  const known = Object.keys(map).filter((k) => k in obj);
  const unknown = Object.keys(obj).filter((k) => !(k in map) && !skip.has(k));
  return [...known, ...unknown];
}
